import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { AgentAutoPublishCountdown } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-auto-publish-countdown';
import { AgentPublishConfirmationAlertContent } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-publish-confirmation-alert-content';
import { createStyles } from '@/contexts/theme-context';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingPublishButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-publish-button';
import { PricingRecommendationRow } from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import { PricingUndoButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-undo-button';
import { RecommendationActionsSnapshot } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { useAlertContext } from '@usepepper/sprout-kit-ui';
import { useExpirationCountdown } from '@sales-portal/blocs/hooks/use-expiration-countdown';
import { usePublishableActions } from '@/experiences/sales-portal/domain/pricing-agent/blocs/hooks/use-publishable-actions';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';
import { View } from 'react-native';

interface Props {
  autoPublishAt: string | null;
  isPublishing: boolean;
  isPublishingEnabled: boolean;
  onPublish: (actions: RecommendationActionsSnapshot) => void;
  rows: PricingRecommendationRow[];
}

export const AgentReviewActions: React.FC<Props> = ({
  autoPublishAt,
  isPublishing,
  isPublishingEnabled,
  onPublish,
  rows,
}) => {
  const [styles] = useStyles();

  const millisecondsUntilAutoPublish = useExpirationCountdown(autoPublishAt);

  const isAutoAccepted = autoPublishAt !== null;

  // Auto-accepted rows have no untouched state, so Publish covers every row on
  // the screen rather than just the ones the user interacted with.
  const { counts, hasPublishableActions, publishableActions } =
    usePublishableActions({
      isAutoAccepted,
      rows,
    });

  const undo = useRecommendationActionsStore((state) => state.undo);
  const canUndo = useRecommendationActionsStore(
    (state) => state.actionSnapshotStack.length > 0,
  );

  const { alert } = useAlertContext('agent_publish');

  const handleUndo = useCallback(() => {
    undo();
    pricingManagementAnalytics.track(PricingAnalyticEvent.AGENT_UNDO, {});
  }, [undo]);

  const onInitiatePublish = useCallback(() => {
    alert({
      buttons: [
        {
          action: () => {},
          style: 'cancel',
          title: i18n.t('cancel'),
        },
        {
          action: () => onPublish(publishableActions),
          style: 'default',
          title: i18n.t('pricing.publish.modal.cta'),
        },
      ],
      content: (
        <AgentPublishConfirmationAlertContent
          acceptedCount={counts.acceptedCount}
          overriddenCount={counts.overriddenCount}
          rejectedCount={counts.rejectedCount}
        />
      ),
      message: '',
      title: i18n.t('pricing.publish.modal.title'),
    });
  }, [alert, counts, onPublish, publishableActions]);

  return (
    <View style={styles.container}>
      {millisecondsUntilAutoPublish !== null ? (
        <AgentAutoPublishCountdown
          millisecondsRemaining={millisecondsUntilAutoPublish}
        />
      ) : null}
      {canUndo ? (
        <PricingUndoButton disabled={isPublishing} onPress={handleUndo} />
      ) : null}
      <PricingPublishButton
        disabled={
          !isPublishingEnabled || !hasPublishableActions || isPublishing
        }
        label={isAutoAccepted ? i18n.t('pricing.agent.publish.cta') : undefined}
        loading={isPublishing}
        onPress={onInitiatePublish}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
